import { useSplitView } from '@/app/_common/split-view/split-view-context'
import { WarningCircleIcon } from '@/components/icons/warning-circle-icon'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'
import { Workflow } from '@/hooks/api/use-all-workflows-query'
import { useDeleteWorkflowMutation } from '@/hooks/api/use-delete-workflow-mutation'
import { T, useTranslate } from '@tolgee/react'
import { isChatContextTab } from './split-view/tabs/types'

export function DeleteChatDialog({
  open,
  onOpenChange,
  workflowId,
  workflowTitle,
}: Readonly<{
  open: boolean
  onOpenChange: SetState<boolean>
  workflowId: Workflow['workflowId']
  workflowTitle: Workflow['title']
}>) {
  const { t } = useTranslate()
  const { filterTabs } = useSplitView()
  const { isPending, mutateAsync } = useDeleteWorkflowMutation({ workflowId })

  const handleDelete = async () => {
    try {
      await mutateAsync()

      filterTabs({ predicate: (t) => isChatContextTab(t) && t.workflowId === workflowId })
      onOpenChange(false)

      toast.success(t('chat.deleteDialog.toast.success'))
    } catch (error) {
      toast.error(t('chat.deleteDialog.toast.error'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby='' className='gap-2'>
        <DialogHeader>
          <DialogTitle className='text-2xl'>
            <T keyName='chat.deleteDialog.title' />{' '}
            <span className='font-[600]'>{workflowTitle}</span>?
          </DialogTitle>
        </DialogHeader>
        <div className='my-8 flex gap-2'>
          <div>
            <WarningCircleIcon className='shrink-0 text-error-300' />
          </div>
          <div className='flex flex-col gap-6'>
            <p>
              <T keyName='chat.deleteDialog.warning' />
            </p>
            <p className='font-light'>
              <T keyName='chat.deleteDialog.description' />
            </p>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button
              disabled={isPending}
              variant='mango-outline'
              title={t('shared.cancelDialog.a11y')}
            >
              <T keyName='shared.cancelDialog.title' />
            </Button>
          </DialogClose>
          <Button
            type='submit'
            disabled={isPending}
            title={t('chat.deleteDialog.button.title')}
            onClick={handleDelete}
          >
            <T keyName='chat.deleteDialog.button.label' />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
